import React, { useCallback, useEffect } from 'react';
import { Modal } from '@material-ui/core';
import styled from 'styled-components';

import DownloadIcon from '../../../assets/images/download-white.svg';
import { Zephyr } from '../../../utils/interface';
import useReports from './hooks';
import { formatReportDate } from './utils';
import { Report } from './types';
import Reporting from '../reporting/page';
import { convertToTimestampMeasurementDataRange } from '../reporting/utils';
import utilsRequest from '../../../utils/request';
import analyticsEventFirer from '../../../utils/functions/analyticsEventFirer';
import { gtmEventIdentifiers } from '../../../utils/consts';

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 0;
  outline: none;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e0e0e0;
`;

const ModalTitle = styled.h4`
  margin: 0;
  font-size: 14px;

  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
`;

const ModalContent = styled.div`
  padding: 24px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #cbd5e0;
  background-color: #f7fafc;
`;

const InstructionText = styled.p`
  margin-top: 0;
  font-size: 1.4rem;
  color: #333;
`;

const StatusText = styled.p`
  margin: 24px 0;
  font-size: 1.4rem;
  font-weight: 600;
  color: #333;
`;

const DateSelection = styled.div`
  margin: 24px 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  font-size: 1.4rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const DateButton = styled.button<{ active?: boolean }>`
  background-color: ${(props) =>
    props.active ? 'var(--color-primary)' : '#edf1f9'};
  color: ${(props) => (props.active ? 'white' : '#333')};
  border: 1px solid #718fcf;
  border-radius: 20px;
  padding: 10px 24px;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;

  &:hover {
    background-color: ${(props) =>
      props.active ? 'var(--color-primary)' : '#e0e0e0'};
  }
`;

const CancelButton = styled.button`
  background: none;
  border: none;
  color: var(--color-primary);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 16px;
`;

export const DownloadButton = styled.button`
  background-color: var(--color-primary);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
`;

const PopupWarning = styled.div`
  margin: 10px 0;
  font-size: 1.4rem;
  position: relative;
  display: flex;
  align-items: flex-start;

  & > p {
    margin: 6px 0;
  }
`;

type Props = {
  curZephyr: Zephyr;
  bearerToken: string | null;
  userReports: { [key: number]: Report[] };
  setUserReports: React.Dispatch<
    React.SetStateAction<{ [key: number]: Report[] }>
  >;
};

export default function DownloadMeasurementModal({
  curZephyr,
  bearerToken,
  userReports,
  setUserReports,
}: Props) {
  const { data: reports, isLoading, error } = useReports();
  const [open, setOpen] = React.useState(false);
  const [selectedReport, setSelectedReport] = React.useState<Report | null>(
    null,
  );
  const { zNumber } = curZephyr;

  const isSameMonth = useCallback((date1, date2) => {
    const [year1, month1] = date1.split('-');
    const [year2, month2] = date2.split('-');

    return year1 === year2 && month1 === month2;
  }, []);

  useEffect(() => {
    const getAvailableMonths = async () => {
      const { start: startDate } = convertToTimestampMeasurementDataRange(
        reports[0].reportDate,
      );
      const { end: endDate } = convertToTimestampMeasurementDataRange(
        reports[reports.length - 1].reportDate,
      );
      const measurementData = await utilsRequest.getBCCPublicMeasurementData(
        zNumber,
        String(startDate),
        String(endDate),
        1,
        bearerToken as string,
      );
      if (
        !measurementData ||
        (!measurementData.hasOwnProperty('data') &&
          !measurementData.hasOwnProperty('Hourly average on the hour'))
      ) {
        setUserReports((prevReports) => {
          return { ...prevReports, [zNumber]: [] };
        });
        return;
      }
      const filteredReports: Report[] = [];
      reports.forEach(
        (item: {
          addedBy: string;
          addedOn: string;
          reportDate: string;
          text: string;
        }) => {
          const { slotA, slotB } = measurementData.data[
            'Hourly average on the hour'
          ];
          const areSlotsValid = slotA || slotB;
          let isDataNullable;
          if (areSlotsValid) {
            const formattedReportDate = item.reportDate.split('T')[0];
            const activeSlot =
              slotA && slotA.hasOwnProperty('ambTempC') ? slotA : slotB;
            const {
              dateTime,
              ambTempC,
              tempC,
              NO2,
              O3,
              particulatePM25,
              particulatePM10,
            } = activeSlot;
            let startIdx: number | null = null;
            let endIdx: number | null = null;
            for (let i = 0; i < dateTime.data.length; i++) {
              const formatDate = dateTime.data[i].split('T')[0];
              if (isSameMonth(formattedReportDate, formatDate)) {
                if (typeof startIdx !== 'number') {
                  startIdx = i;
                } else if (
                  i === dateTime.data.length - 1 ||
                  !isSameMonth(
                    formattedReportDate,
                    dateTime.data[i + 1].split('T')[0],
                  )
                ) {
                  endIdx = i;
                  break;
                }
              }
            }
            const slicedData = (ambTempC && ambTempC.data
              ? ambTempC.data
              : tempC.data
            ).slice(Number(startIdx), Number(endIdx) + 1);
            isDataNullable = slicedData.every(
              (val: number | null) => val === null,
            );
            if (!isDataNullable) {
              const slicedDateTime = dateTime.data.slice(
                Number(startIdx),
                Number(endIdx) + 1,
              );
              const slicedNO2 = NO2.data.slice(
                Number(startIdx),
                Number(endIdx) + 1,
              );
              const slicedO3 = O3.data.slice(
                Number(startIdx),
                Number(endIdx) + 1,
              );
              const slicedPM25 = particulatePM25.data.slice(
                Number(startIdx),
                Number(endIdx) + 1,
              );
              const slicedPM10 = particulatePM10.data.slice(
                Number(startIdx),
                Number(endIdx) + 1,
              );
              filteredReports.push({
                ...item,
                measurementData: {
                  dateTime: { ...dateTime, data: slicedDateTime },
                  NO2: { ...NO2, data: slicedNO2 },
                  O3: { ...O3, data: slicedO3 },
                  particulatePM25: { ...particulatePM25, data: slicedPM25 },
                  particulatePM10: { ...particulatePM10, data: slicedPM10 },
                },
              });
            }
          }
        },
      );
      setUserReports((prevReports) => {
        return { ...prevReports, [zNumber]: filteredReports };
      });
    };
    if (
      open &&
      reports.length &&
      bearerToken &&
      !userReports.hasOwnProperty(zNumber)
    )
      getAvailableMonths();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reports, bearerToken]);

  const handleClose = () => {
    setOpen(false);
    setSelectedReport(null);
  };

  if (isLoading || error) {
    return null;
  }

  const hasData = Object.values(reports).length > 0;

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>Download School Air Quality Report</ModalTitle>
            <CloseButton onClick={handleClose} type="button">
              X
            </CloseButton>
          </ModalHeader>

          <ModalContent>
            <InstructionText>
              {hasData ? (
                <>
                  Select a report date from the list below. Once selected, click
                  &apos;Download&apos; to open the report in a new tab.
                </>
              ) : (
                <>
                  Reports are generated on a monthly basis, check back here
                  soon.
                </>
              )}
            </InstructionText>

            {hasData && (
              <>
                {isLoading || !userReports[zNumber] ? (
                  <StatusText>Loading reports...</StatusText>
                ) : userReports[zNumber] && userReports[zNumber].length ? (
                  <DateSelection>
                    <>
                      {userReports[zNumber].map((report) => (
                        <DateButton
                          key={report.reportDate}
                          type="button"
                          active={
                            selectedReport?.reportDate === report.reportDate
                          }
                          onClick={() => {
                            setSelectedReport(report);
                          }}
                        >
                          {formatReportDate(report.reportDate)}
                        </DateButton>
                      ))}
                    </>
                  </DateSelection>
                ) : (
                  <StatusText>No reports available for this device.</StatusText>
                )}

                <PopupWarning>
                  <p>
                    If your report doesn&apos;t open, your browser&apos;s popup
                    blocker might be preventing it. Please check your settings
                    and try again.
                  </p>
                </PopupWarning>
              </>
            )}
          </ModalContent>

          <ModalFooter>
            <CancelButton type="button" onClick={handleClose}>
              Cancel
            </CancelButton>
            <Reporting
              hasData={hasData}
              reports={userReports[zNumber]}
              selectedReport={selectedReport}
              bearerToken={bearerToken}
              curZephyr={curZephyr}
            />
          </ModalFooter>
        </ModalContainer>
      </Modal>

      <button
        className="button primary flex"
        style={{
          padding: 12,
          alignItems: 'center',
          gap: 4,
          color: 'white',
          width: '250px',
        }}
        type="button"
        onClick={() => {
          setOpen(true);
          analyticsEventFirer(gtmEventIdentifiers.reporting);
        }}
      >
        <img src={DownloadIcon} width={20} height={20} alt="Download" />
        Download Air Quality Report
      </button>
    </>
  );
}
